import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdRefresh, MdWarning } from 'react-icons/md';
import { BsBuilding, BsCalendar2, BsCurrencyRupee } from 'react-icons/bs';
import { FiFileText } from 'react-icons/fi';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import { TableSkeleton } from '../components/Table/TableSkeleton';
import axiosInstance from '../axios';
import toast from 'react-hot-toast';

interface BilledProjectWithoutInvoice {
  projectId: string;
  projectNumber: string;
  projectName: string;
  projectAmount: number;
  customerName: string;
  billedDate: string;
  billedBy: string;
  note: string;
}

interface DiagnosticData {
  projects: BilledProjectWithoutInvoice[];
  totalCount: number;
  totalAmount: number;
  message: string;
}

const BillingDiagnostic: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<DiagnosticData | null>(null);
  const navigate = useNavigate();

  const fetchDiagnosticData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/v1/admin/project/billed-without-invoices');
      
      if (response.data.success) {
        setData(response.data.data);
        
        if (response.data.data.totalCount > 0) {
          toast.error(`Found ${response.data.data.totalCount} discrepancies!`);
        } else {
          toast.success('No discrepancies found!');
        }
      }
    } catch (error: any) {
      console.error('Error fetching diagnostic data:', error);
      toast.error('Failed to fetch diagnostic data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnosticData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const columns = [
    {
      header: 'Project Number',
      accessor: 'projectNumber',
      cell: (row: BilledProjectWithoutInvoice) => (
        <div className="flex items-center gap-2">
          <FiFileText className="text-gray-500" />
          <button
            onClick={() => handleProjectClick(row.projectId)}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            {row.projectNumber}
          </button>
        </div>
      ),
    },
    {
      header: 'Project Name',
      accessor: 'projectName',
      cell: (row: BilledProjectWithoutInvoice) => (
        <div className="max-w-xs truncate" title={row.projectName}>
          {row.projectName}
        </div>
      ),
    },
    {
      header: 'Customer',
      accessor: 'customerName',
      cell: (row: BilledProjectWithoutInvoice) => (
        <div className="flex items-center gap-2">
          <BsBuilding className="text-gray-500" />
          <span>{row.customerName}</span>
        </div>
      ),
    },
    {
      header: 'Amount',
      accessor: 'projectAmount',
      cell: (row: BilledProjectWithoutInvoice) => (
        <div className="flex items-center gap-1 font-semibold text-gray-900">
          <BsCurrencyRupee />
          <span>{row.projectAmount.toLocaleString('en-IN')}</span>
        </div>
      ),
    },
    {
      header: 'Billed Date',
      accessor: 'billedDate',
      cell: (row: BilledProjectWithoutInvoice) => (
        <div className="flex items-center gap-2">
          <BsCalendar2 className="text-gray-500" />
          <span>{formatDate(row.billedDate)}</span>
        </div>
      ),
    },
    {
      header: 'Billed By',
      accessor: 'billedBy',
      cell: (row: BilledProjectWithoutInvoice) => (
        <span className="text-gray-700">{row.billedBy}</span>
      ),
    },
    {
      header: 'Note',
      accessor: 'note',
      cell: (row: BilledProjectWithoutInvoice) => (
        <div className="max-w-xs truncate text-gray-600 text-sm" title={row.note}>
          {row.note || '-'}
        </div>
      ),
    },
  ];

  return (
    <ComponentWrapper>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <MdWarning className="text-3xl text-orange-500" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Billing Diagnostic Report
                </h1>
                <p className="text-sm text-gray-600">
                  Projects marked as BILLED but without invoices
                </p>
              </div>
            </div>
            <button
              onClick={fetchDiagnosticData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <MdRefresh className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {/* Summary Cards */}
          {data && !loading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">
                      Projects with Discrepancies
                    </p>
                    <p className="text-3xl font-bold text-orange-700 mt-1">
                      {data.totalCount}
                    </p>
                  </div>
                  <MdWarning className="text-4xl text-orange-400" />
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">
                      Total Amount Affected
                    </p>
                    <p className="text-3xl font-bold text-red-700 mt-1">
                      {formatCurrency(data.totalAmount)}
                    </p>
                  </div>
                  <BsCurrencyRupee className="text-4xl text-red-400" />
                </div>
              </div>

              <div className={`${data.totalCount === 0 ? 'bg-green-50 border-green-500' : 'bg-blue-50 border-blue-500'} border-l-4 p-4 rounded-lg`}>
                <div>
                  <p className={`text-sm font-medium ${data.totalCount === 0 ? 'text-green-600' : 'text-blue-600'}`}>
                    Status
                  </p>
                  <p className={`text-sm mt-2 ${data.totalCount === 0 ? 'text-green-700' : 'text-blue-700'}`}>
                    {data.message}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <TableSkeleton rows={5} columns={7} />
        ) : data && data.totalCount > 0 ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 bg-yellow-50 border-b border-yellow-200">
              <div className="flex items-center gap-2 text-yellow-800">
                <MdWarning />
                <p className="text-sm font-medium">
                  These projects are marked as BILLED in the system but do not have corresponding invoices.
                  This causes discrepancies between dashboard metrics and billing history.
                </p>
              </div>
            </div>
            <ResponsiveTable
              columns={columns}
              data={data.projects}
              onRowClick={(row: BilledProjectWithoutInvoice) => handleProjectClick(row.projectId)}
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No Discrepancies Found
                </h3>
                <p className="text-gray-600">
                  All projects marked as BILLED have corresponding invoices.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ComponentWrapper>
  );
};

export default BillingDiagnostic;
